<template>
	<div class="ks-menu" :class="{ 'ks-menu-disabled': disabled }">
		<div ref="activator" class="ks-menu-activator" :class="{ 'ks-menu-activator-full-width': fullWidth }" @click="onActivatorClick" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
			<slot name="activator" :on="activatorListeners" :value="internalOpen"></slot>
		</div>
	</div>
</template>

<script>
import { popupMixin, getPopupContainer } from "@/lib/popupStack"

export default {
	mixins: [popupMixin],
	props: {
		"disabled": {
			type: Boolean,
			default: false
		},
		"openOnHover": {
			type: Boolean,
			default: false
		},
		"closeOnClick": {
			type: Boolean,
			default: true
		},
		"closeOnContentClick": {
			type: Boolean,
			default: true
		},
		"offsetX": {
			type: Boolean,
			default: false
		},
		"offsetY": {
			type: Boolean,
			default: false
		},
		"left": {
			type: Boolean,
			default: false
		},
		"right": {
			type: Boolean,
			default: false
		},
		"top": {
			type: Boolean,
			default: false
		},
		"bottom": {
			type: Boolean,
			default: true
		},
		"nudgeLeft": {
			type: [Number, String],
			default: 0
		},
		"nudgeRight": {
			type: [Number, String],
			default: 0
		},
		"nudgeTop": {
			type: [Number, String],
			default: 0
		},
		"nudgeBottom": {
			type: [Number, String],
			default: 0
		},
		"minWidth": {
			type: [Number, String],
			default: ""
		},
		"nudgeWidth": {
			type: [Number, String],
			default: 0
		},
		"fullWidth": {
			type: Boolean,
			default: false
		},
		"transition": {
			type: [String, Boolean],
			default: ""
		},
		"maxWidth": {
			type: [Number, String],
			default: ""
		},
		"maxHeight": {
			type: [Number, String],
			default: ""
		},
		"value": {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			internalOpen: false,
			posTop: 0,
			posLeft: 0,
			activatorWidth: 0,
			contentEl: null,
			stackZIndex: null
		}
	},
	watch: {
		value (val) {
			if (val && !this.internalOpen) {
				this.open()
			} else if (!val && this.internalOpen) {
				this.close()
			}
		}
	},
	computed: {
		"activatorListeners" () {
			var vm = this
			return {
				click (e) {
					if (vm.openOnHover) return
					if (e && e.stopPropagation) e.stopPropagation()
					vm.toggle()
				}
			}
		}
	},
	methods: {
		updatePosition () {
			if (!this.$refs.activator || !this.contentEl) return
			var rect = this.$refs.activator.getBoundingClientRect()
			this.activatorWidth = rect.width
			var nudgeX = Number(this.nudgeRight) - Number(this.nudgeLeft)
			var nudgeY = Number(this.nudgeBottom) - Number(this.nudgeTop)

			// Alustava sijoitusasetus (pystysuunta)
			if (this.offsetX) {
				this.posTop = rect.top + nudgeY
				if (this.left) {
					this.posLeft = rect.left + nudgeX
				} else {
					this.posLeft = rect.right + nudgeX
				}
			} else {
				if (this.top && !this.bottom) {
					this.posTop = rect.top + nudgeY
				} else {
					this.posTop = rect.bottom + nudgeY
				}
				if (this.right) {
					this.posLeft = rect.right + nudgeX
				} else {
					this.posLeft = rect.left + nudgeX
				}
			}

			// Hienosäätö ja rajoitukset kun sisältö on renderöity
			this.$nextTick(() => {
				if (!this.contentEl) return
				var contentRect = this.contentEl.getBoundingClientRect()
				
				if (this.offsetX && this.left) {
					this.posLeft = rect.left - contentRect.width + nudgeX
				}
				if (!this.offsetX && this.right) {
					this.posLeft = rect.right - contentRect.width + nudgeX
				}
				if (this.top && !this.bottom && !this.offsetX) {
					this.posTop = rect.top - contentRect.height + nudgeY
				}

				// 1. Vaakasuuntainen rajoitus (Viewport width)
				var viewportWidth = window.innerWidth
				if (this.posLeft + contentRect.width > viewportWidth) {
					this.posLeft = viewportWidth - contentRect.width - 4
				}
				if (this.posLeft < 0) {
					this.posLeft = 4
				}

				// 2. Pystysuuntainen rajoitus (Viewport height) -> UUSI LOGIIKKA
				var viewportHeight = window.innerHeight

				// Älykäs kääntö: Jos valikko on aukeamassa alas, mutta ei mahdu, 
				// ja yläpuolella on enemmän tilaa, käännetään se aktivaattorin yläpuolelle.
				if (!this.top && (this.posTop + contentRect.height > viewportHeight)) {
					if (rect.top > viewportHeight - rect.bottom) {
						this.posTop = rect.top - contentRect.height + nudgeY
					}
				}
				// Jos valikko on aukeamassa ylös, mutta ei mahdu, käännetään se alas.
				else if (this.top && (this.posTop < 0)) {
					if (viewportHeight - rect.bottom > rect.top) {
						this.posTop = rect.bottom + nudgeY
					}
				}

				// Lopullinen pakotettu rajoitus (Clamping), ettei valikko mene koskaan yli ruudun reunojen
				if (this.posTop + contentRect.height > viewportHeight) {
					this.posTop = viewportHeight - contentRect.height - 4
				}
				if (this.posTop < 0) {
					this.posTop = 4
				}

				// Sovletetaan lasketut tyylit
				this.applyContentStyle()
			})
		},
		open () {
			if (this.disabled || this.internalOpen) return
			this.internalOpen = true
			this.stackZIndex = this.registerPopup()
			this.$emit("input", true)
			this.$emit("open")
			this.$nextTick(() => {
				this.createContentEl()
				this.updatePosition()
			})
			window.addEventListener("scroll", this.updatePosition, true)
			window.addEventListener("resize", this.updatePosition)
		},
		close () {
			if (!this.internalOpen) return
			this.internalOpen = false
			this.unregisterPopup()
			this.$emit("input", false)
			this.$emit("close")
			this.destroyContentEl()
			window.removeEventListener("scroll", this.updatePosition, true)
			window.removeEventListener("resize", this.updatePosition)
		},
		toggle () {
			if (this.internalOpen) {
				this.close()
			} else {
				this.open()
			}
		},
		onActivatorClick () {
			if (this.openOnHover) return
			this.toggle()
		},
		onMouseEnter () {
			if (this.openOnHover) {
				this.open()
			}
		},
		onMouseLeave () {
			if (this.openOnHover) {
				this.hoverTimeout = setTimeout(() => {
					this.close()
				}, 150)
			}
		},
		onContentMouseEnter () {
			if (this.openOnHover && this.hoverTimeout) {
				clearTimeout(this.hoverTimeout)
			}
		},
		onContentMouseLeave () {
			if (this.openOnHover) {
				this.close()
			}
		},
		onContentClick () {
			if (this.closeOnContentClick) {
				this.close()
			}
		},
		popupElements () {
			return [this.$refs.activator, this.contentEl]
		},
		onPopupOutsideClick () {
			if (this.closeOnClick) this.close()
		},
		onPopupEscape () {
			if (this.closeOnClick) this.close()
		},
		createContentEl () {
			if (this.contentEl || !this.internalOpen) return
			var el = document.createElement("div")
			el.className = "ks-menu-content ks-floating-menu"
			el.addEventListener("mouseenter", this.onContentMouseEnter)
			el.addEventListener("mouseleave", this.onContentMouseLeave)
			el.addEventListener("click", this.onContentClick)
			getPopupContainer().appendChild(el)
			this.contentEl = el
			this.contentVm = new this.$root.constructor({
				parent: this,
				render: (h) => {
					var nodes = this.$slots.default
					return h("div", { style: { display: "contents" } }, nodes)
				}
			})
			this.contentVm.$mount()
			el.appendChild(this.contentVm.$el)
			this.applyContentStyle()
		},
		destroyContentEl () {
			if (!this.contentEl) return
			if (this.contentVm) {
				this.contentVm.$destroy()
				this.contentVm = null
			}
			this.contentEl.removeEventListener("mouseenter", this.onContentMouseEnter)
			this.contentEl.removeEventListener("mouseleave", this.onContentMouseLeave)
			this.contentEl.removeEventListener("click", this.onContentClick)
			this.contentEl.remove()
			this.contentEl = null
		},
		applyContentStyle () {
			if (!this.contentEl) return
			var style = this.contentEl.style
			style.zIndex = this.stackZIndex
			style.top = this.posTop + "px"
			style.left = this.posLeft + "px"
			var nudgeWidth = Number(this.nudgeWidth) || 0
			if (this.minWidth) {
				style.minWidth = typeof this.minWidth === "number" ? this.minWidth + "px" : this.minWidth
			} else if ((this.fullWidth || nudgeWidth) && this.activatorWidth) {
				style.minWidth = (this.activatorWidth + nudgeWidth) + "px"
			}
			if (this.maxWidth) {
				style.maxWidth = typeof this.maxWidth === "number" ? this.maxWidth + "px" : this.maxWidth
			}
			if (this.maxHeight) {
				style.maxHeight = typeof this.maxHeight === "number" ? this.maxHeight + "px" : this.maxHeight
				style.overflowY = "auto"
			}
		}
	},
	updated () {
		if (!this.internalOpen || !this.contentVm) return
		this.contentVm.$forceUpdate()
		this.updatePosition()
	},
	beforeDestroy () {
		this.destroyContentEl()
		window.removeEventListener("scroll", this.updatePosition, true)
		window.removeEventListener("resize", this.updatePosition)
		if (this.hoverTimeout) {
			clearTimeout(this.hoverTimeout)
		}
	}
}
</script>

<style>
.ks-menu {
	display: inline-block;
	position: relative;
}

.ks-menu-disabled {
	pointer-events: none;
	opacity: 0.5;
}

.ks-menu-activator {
	display: inline-flex;
	align-items: center;
	cursor: pointer;
}

.ks-menu-activator-full-width {
	display: flex;
	width: 100%;
}

.ks-menu-content {
	position: fixed;
	min-width: 120px;
}
</style>